// The admin dashboard's modal width scale — ONE definition, read by every admin
// overlay that exposes a width choice (BUG-1620).
//
// It lived inline in `Modal.tsx`'s dialect until `ConfirmDialog` needed a width
// choice of its own. Copying the seven `max-w-*` classes across would have made
// "how wide is a `lg` dialog" true in two places that agree today and can
// silently disagree tomorrow — the same two-places-disagree hazard the dialect's
// gutter comment already documents, and exactly what
// ADR 044 — Must Consolidate exists to stop. One map, two consumers.
//
// These are Tailwind's own `max-w-*` scale rather than a bespoke set: a reader
// who knows `max-w-lg` already knows how wide `size="lg"` is, and there is no
// second vocabulary to learn.

import type { PanelHeaderWidth } from './PanelHeader';

/** The width keys an admin overlay may be asked for. */
export type AdminModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '5xl';

/**
 * Width key → the Tailwind class that binds it.
 *
 * Consumed by `Modal`'s dialect (via the shared primitive's `dialect.size`
 * lookup) and by `ConfirmDialog`, which composes the class onto its panel
 * directly because the shared confirm core renders no modal of its own.
 */
export const ADMIN_MODAL_SIZES: Record<AdminModalSize, string> = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '5xl': 'max-w-5xl',
};

/** The sizes whose window is at most 512px: their header takes the narrow title (BL-2915). */
const NARROW_SIZES: ReadonlySet<AdminModalSize> = new Set<AdminModalSize>(['sm', 'md', 'lg']);

/**
 * The header width class a Modal of this size draws (BL-2915): up to `lg`
 * (512px) is `narrow`, `xl` (576px) and up is `wide`. No size draws no width
 * class, so it stays `wide`, as before.
 *
 * @param size - The Modal's `size`, if any.
 * @returns `narrow` or `wide`.
 */
export function headerWidthForSize(size: AdminModalSize | undefined): PanelHeaderWidth {
    return size != null && NARROW_SIZES.has(size) ? 'narrow' : 'wide';
}
